import { useCallback, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';
import { toast } from 'sonner';
import { SurveySubmissionSource } from '@shared/surveySubmissionSource';
import { isConsumerSmsMfaEnabled } from '@shared';

import {
  useInviteClientTrigger,
  useInviteClientStore,
  agentEnteredIntakeApi,
  quickClientApi,
} from '@/features/invite-client';
import { usePilotAgentEnteredIntake } from '@/shared/hooks/use-active-pilot-program';
import {
  selectFeatureFlags,
  selectSessionAdvisor,
  fetchClientsForAdvisorByAdvisorIdRequest,
} from '@client/model';
import { toDateOnlyString } from '@/shared/lib/utils';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@/util/clerkAuthHelpers';

export const fillIntakeFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

/**
 * Two schemas rather than one with optional fields refined at submit time.
 *
 * `form.formState.isValid` drives the submit button, and with optional fields plus a
 * `superRefine` it would read true before the agent had typed an email or a date of birth. Two
 * schemas keep that signal honest, at the cost of a union on the value type.
 */
export const agentEnteredIntakeFormSchema = fillIntakeFormSchema.extend({
  email: z.string().email('A valid email address is required'),
  birthDate: z.date({ error: 'Date of birth is required' }),
});

export type FillIntakeFormValues = z.infer<typeof fillIntakeFormSchema>;
export type AgentEnteredIntakeFormValues = z.infer<typeof agentEnteredIntakeFormSchema>;


export function useFillIntakeForm() {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorFeatureFlags = useSelector(selectFeatureFlags);
  const { flags } = useFeatureFlags();
  const { clearInviteClientParams } = useInviteClientTrigger();
  const { agentEnteredIntakeId, setAgentEnteredIntakeId, setIsAgentEnteredIntakeConsented } =
    useInviteClientStore();
  const isAgentEntered = !!usePilotAgentEnteredIntake();
  const isConsumerSmsMfaAvailable = isConsumerSmsMfaEnabled({
    advisorFeatureFlags,
    appConfigFlags: flags,
  });
  const [phoneCountryCode, setPhoneCountryCode] = useState<SupportedCountryCode>('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const requiresMobileMfa = isAgentEntered && isConsumerSmsMfaAvailable;
  const phoneError = requiresMobileMfa
    ? validateForCountry(stripDigits(phoneNumber), phoneCountryCode)
    : null;

  const resolver: Resolver<AgentEnteredIntakeFormValues> = isAgentEntered
    ? zodResolver(agentEnteredIntakeFormSchema)
    : (zodResolver(fillIntakeFormSchema) as unknown as Resolver<AgentEnteredIntakeFormValues>);
  const form = useForm<AgentEnteredIntakeFormValues>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
    } as AgentEnteredIntakeFormValues,
  });

  const refreshClients = useCallback(() => {
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: true,
      }),
    );
    dispatch({ type: 'RESUME_SESSION_REFRESH' });
  }, [advisor.advisorId, dispatch]);

  const quickClientMutation = useMutation({
    mutationFn: quickClientApi.createQuickClientWithForm,
    onSuccess: data => {
      refreshClients();
      clearInviteClientParams();
      form.reset();
      const baseUrl = window.location.origin;
      const formUrl = `${baseUrl}/onboarding-form/${data.formReferenceId}?source=${SurveySubmissionSource.FILL_FORM}`;
      window.open(formUrl, '_blank', 'noopener,noreferrer');
      toast.success('Client created successfully', {
        description: 'The intake form has been opened in a new tab.',
      });
    },
    onError: () => {
      toast.error('Failed to create client', {
        description: 'Please try again or contact support',
      });
    },
  });

  const agentEnteredMutation = useMutation({
    mutationFn: agentEnteredIntakeApi.create,
    // Deliberately opens nothing. The whole point of this path is that the questionnaire stays
    // shut until the customer has given permission.
    onSuccess: data => {
      refreshClients();
      setAgentEnteredIntakeId(data.agentEnteredIntakeId);
      setIsAgentEnteredIntakeConsented(false);

      if (data.reused) return;

      if (data.status === 'undeliverable') {
        toast.error('We couldn’t email that address', {
          description: 'It has unsubscribed from Waterlily emails. Try another address.',
        });
        return;
      }

      if (data.status === 'send-failed') {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. Try sending it again.',
        });
        return;
      }

      toast.success('Permission request sent', {
        description: `We emailed ${data.sentTo}. You can open the form once they accept.`,
      });
    },
    onError: (error: unknown) => {
      toast.error('Failed to send the permission request', {
        description:
          error instanceof Error ? error.message : 'Please try again or contact support',
      });
    },
  });

  function onSubmit(data: AgentEnteredIntakeFormValues) {
    if (isAgentEntered) {
      agentEnteredMutation.mutate({
        advisorId: advisor.advisorId,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        birthDate: toDateOnlyString(data.birthDate),
        intakeMode: 'intake-form',
        consumerSmsMfa: requiresMobileMfa
          ? { phoneNumber: `${phoneCountryCode}${stripDigits(phoneNumber)}` }
          : undefined,
      });
      return;
    }

    quickClientMutation.mutate({
      advisorId: advisor.advisorId,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
    });
  }

  const startOver = useCallback(() => {
    setAgentEnteredIntakeId(null);
    setIsAgentEnteredIntakeConsented(false);
    form.reset();
    setPhoneCountryCode('+1');
    setPhoneNumber('');
    setIsPhoneTouched(false);
    refreshClients();
  }, [form, refreshClients, setAgentEnteredIntakeId, setIsAgentEnteredIntakeConsented]);

  const handleConsentStatusChange = useCallback(
    (isConsented: boolean) => {
      setIsAgentEnteredIntakeConsented(isConsented);
      if (isConsented) refreshClients();
    },
    [refreshClients, setIsAgentEnteredIntakeConsented],
  );

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: quickClientMutation.isPending || agentEnteredMutation.isPending,
    isAgentEntered,
    isConsumerSmsMfaAvailable: isAgentEntered && isConsumerSmsMfaAvailable,
    phoneCountryCode,
    setPhoneCountryCode: (countryCode: SupportedCountryCode) => {
      setPhoneCountryCode(countryCode);
      setPhoneNumber(current => formatByCountry(stripDigits(current), countryCode));
    },
    phoneNumber,
    setPhoneNumber: (value: string) =>
      setPhoneNumber(formatByCountry(stripDigits(value), phoneCountryCode)),
    isPhoneTouched,
    touchPhone: () => setIsPhoneTouched(true),
    phoneError,
    canSubmit: form.formState.isValid && phoneError === null,
    agentEnteredIntakeId,
    startOver,
    handleConsentStatusChange,
  };
}
